/**
 * Quiz Session Manager
 * Handles quiz sessions with user assignment - only assigned users see their sessions.
 */

import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { listQuizFilesFromStorage } from './storageUpload';
import { normalizeQuizDurationMinutes } from './quizDuration';
import { buildWrongAnswersCsv, parsePositiveSerial } from './quizReport';

export {
  QUIZ_DURATION_OPTIONS,
  DEFAULT_QUIZ_DURATION_MINUTES,
  normalizeQuizDurationMinutes,
  quizDurationToSeconds,
  buildQuizPlayerUrl,
} from './quizDuration';

const QUIZ_FILES_COLLECTION = 'quizFiles';
const QUIZ_SESSIONS_COLLECTION = 'quizSessions';
const QUIZ_COMPLETIONS_COLLECTION = 'quizCompletions';
const INTERVIEW_SLOTS_COLLECTION = 'interviewSlots';

/**
 * Save a quiz file metadata to Firestore (after upload to Storage)
 * Used so admin can choose existing files when creating sessions.
 */
export const addQuizFile = async ({ downloadUrl, path, fileName, uploadedBy, durationMinutes }) => {
  if (!db) throw new Error('Firestore not initialized');

  const duration = normalizeQuizDurationMinutes(durationMinutes);
  const docRef = await addDoc(collection(db, QUIZ_FILES_COLLECTION), {
    downloadUrl,
    path,
    fileName,
    uploadedBy: (uploadedBy || '').toLowerCase(),
    uploadedAt: serverTimestamp(),
    durationMinutes: duration,
  });
  return { id: docRef.id, downloadUrl, path, fileName, uploadedBy, durationMinutes: duration };
};

/**
 * Sync Storage files to Firestore quizFiles.
 * Adds any files in Storage (quizzes/) that aren't yet in Firestore.
 * Returns count of newly added files.
 */
export const syncQuizFilesFromStorage = async () => {
  if (!db) return 0;

  const storageFiles = await listQuizFilesFromStorage();
  if (storageFiles.length === 0) return 0;

  const existingDocs = await getDocs(collection(db, QUIZ_FILES_COLLECTION));
  const existingPaths = new Set(existingDocs.docs.map((d) => d.data().path));
  const existingUrls = new Set(existingDocs.docs.map((d) => d.data().downloadUrl));

  let added = 0;
  for (const file of storageFiles) {
    if (existingPaths.has(file.path) || existingUrls.has(file.downloadUrl)) continue;
    await addQuizFile({
      downloadUrl: file.downloadUrl,
      path: file.path,
      fileName: file.fileName,
      uploadedBy: file.uploadedBy,
    });
    added++;
    existingPaths.add(file.path);
    existingUrls.add(file.downloadUrl);
  }
  return added;
};

/**
 * Get all quiz files (for admin to choose existing)
 */
export const getQuizFiles = async () => {
  if (!db) return [];

  const snapshot = await getDocs(collection(db, QUIZ_FILES_COLLECTION));
  const files = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return files.sort((a, b) => {
    const ta = a.uploadedAt?.toMillis?.() ?? 0;
    const tb = b.uploadedAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
};

/**
 * Create a quiz session with assigned users.
 * Only users in assignedUserEmails will see this session.
 */
export const createSession = async ({
  quizFileUrl,
  quizFileName,
  assignedUserEmails = [],
  createdBy,
  title,
  durationMinutes,
}) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!quizFileUrl || typeof quizFileUrl !== 'string' || !quizFileUrl.trim()) {
    throw new Error('Quiz file URL is required');
  }
  if (!createdBy) throw new Error('Creator is required');

  const normalizedEmails = assignedUserEmails.map((e) => (e || '').toLowerCase().trim()).filter(Boolean);
  const duration = normalizeQuizDurationMinutes(durationMinutes);

  const docRef = await addDoc(collection(db, QUIZ_SESSIONS_COLLECTION), {
    quizFileUrl,
    quizFileName: quizFileName || 'Quiz',
    assignedUserEmails: normalizedEmails,
    createdBy: createdBy.toLowerCase(),
    title: title || quizFileName || 'Quiz Session',
    createdAt: serverTimestamp(),
    status: 'active',
    durationMinutes: duration,
  });

  return {
    id: docRef.id,
    quizFileUrl,
    quizFileName,
    assignedUserEmails: normalizedEmails,
    createdBy,
    title,
    status: 'active',
    durationMinutes: duration,
  };
};

/**
 * Get session IDs a user has completed.
 */
export const getCompletedSessionIdsForUser = async (userEmail) => {
  if (!db) return new Set();

  const email = (userEmail || '').toLowerCase();
  if (!email) return new Set();

  const snapshot = await getDocs(
    query(
      collection(db, QUIZ_COMPLETIONS_COLLECTION),
      where('userEmail', '==', email)
    )
  );
  return new Set(snapshot.docs.map((d) => d.data().sessionId));
};

/**
 * Get sessions assigned to a specific user (by email).
 * Excludes sessions the user has already completed.
 */
export const getSessionsForUser = async (userEmail) => {
  if (!db) return [];

  const email = (userEmail || '').toLowerCase();
  if (!email) return [];

  const [sessionsSnapshot, completedIds] = await Promise.all([
    getDocs(
      query(
        collection(db, QUIZ_SESSIONS_COLLECTION),
        where('assignedUserEmails', 'array-contains', email)
      )
    ),
    getCompletedSessionIdsForUser(email),
  ]);

  const sessions = sessionsSnapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => !completedIds.has(s.id));
  return sessions.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
};

/**
 * Upload report CSV to Firebase Storage for easy retrieval by session and user.
 * Path: reports/{sessionId}/{sanitizedEmail}_{timestamp}.csv
 * @returns {Promise<string|null>} Download URL of the uploaded report, or null on failure/missing storage
 */
const uploadReportToStorage = async ({
  sessionId,
  userEmail,
  score,
  totalQuestions,
  sessionTitle,
  answers = [],
}) => {
  if (!storage || typeof storage.ref !== 'function') return null;

  const csv = buildWrongAnswersCsv({
    userEmail,
    sessionTitle,
    score,
    totalQuestions,
    answers,
    completedAt: new Date(),
  });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

  const sanitized = (userEmail || 'user').replace(/[^a-zA-Z0-9.-]/g, '-').slice(0, 60);
  const fileName = `${sanitized}_${Date.now()}.csv`;
  const storagePath = `reports/${sessionId}/${fileName}`;

  try {
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, blob, {
      contentType: 'text/csv;charset=utf-8',
      customMetadata: { userEmail: userEmail || '', sessionTitle: sessionTitle || '' },
    });
    const downloadUrl = await getDownloadURL(snapshot.ref);
    return downloadUrl;
  } catch (err) {
    console.warn('Report upload to Storage failed (Firestore record saved):', err?.message);
    return null;
  }
};

/**
 * Record quiz completion with per-question answers for reports.
 * Saves to Firestore and uploads CSV to Storage.
 * answers: [{ questionId, questionNumber, questionText, selectedAnswerId, selectedAnswerText, correctAnswerId, isCorrect }]
 */
export const recordCompletion = async ({ sessionId, userEmail, score, totalQuestions, sessionTitle, answers = [], playerName = null }) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!sessionId || !userEmail) throw new Error('sessionId and userEmail required');

  const email = (userEmail || '').toLowerCase();

  // Firestore rejects undefined; sanitize answers to replace undefined with null/empty
  const sanitizedAnswers = (answers || []).map((a) => ({
    questionId: a.questionId ?? null,
    questionNumber: parsePositiveSerial(a.questionNumber) ?? parsePositiveSerial(a.questionId) ?? null,
    questionText: a.questionText ?? '',
    selectedAnswerId: a.selectedAnswerId ?? null,
    selectedAnswerText: a.selectedAnswerText ?? '(not answered)',
    correctAnswerId: a.correctAnswerId ?? null,
    correctAnswerText: a.correctAnswerText ?? '',
    isCorrect: Boolean(a.isCorrect),
  }));

  const docRef = await addDoc(collection(db, QUIZ_COMPLETIONS_COLLECTION), {
    sessionId: sessionId ?? '',
    userEmail: email,
    playerName: playerName != null && String(playerName).trim() ? String(playerName).trim() : null,
    completedAt: serverTimestamp(),
    score: score ?? 0,
    totalQuestions: totalQuestions ?? 0,
    sessionTitle: sessionTitle || 'Quiz',
    answers: sanitizedAnswers,
  });

  // Upload report CSV to Storage and store report URL on completion for "View report" link
  const reportUrl = await uploadReportToStorage({
    sessionId,
    userEmail: email,
    score,
    totalQuestions,
    sessionTitle,
    answers: sanitizedAnswers,
  });
  if (reportUrl) {
    await updateDoc(docRef, { reportUrl });
  }
  return docRef.id;
};

/**
 * Delete a quiz session.
 */
export const deleteSession = async (sessionId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!sessionId) throw new Error('sessionId required');

  const docRef = doc(db, QUIZ_SESSIONS_COLLECTION, sessionId);
  await deleteDoc(docRef);
};

/**
 * Delete a quiz completion (report).
 */
export const deleteCompletion = async (completionId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!completionId) throw new Error('completionId required');

  const docRef = doc(db, QUIZ_COMPLETIONS_COLLECTION, completionId);
  await deleteDoc(docRef);
};

/**
 * Delete a quiz file from quiz history (Firestore metadata only; file remains in Storage).
 */
export const deleteQuizFile = async (fileId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!fileId) throw new Error('fileId required');

  const docRef = doc(db, QUIZ_FILES_COLLECTION, fileId);
  await deleteDoc(docRef);
};

/**
 * Get completions for a user (their quiz history).
 */
export const getCompletionsForUser = async (userEmail) => {
  if (!db) return [];

  const email = (userEmail || '').toLowerCase();
  if (!email) return [];

  const snapshot = await getDocs(
    query(
      collection(db, QUIZ_COMPLETIONS_COLLECTION),
      where('userEmail', '==', email)
    )
  );
  const completions = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return completions.sort((a, b) => {
    const ta = a.completedAt?.toMillis?.() ?? 0;
    const tb = b.completedAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
};

/**
 * Get completions for a session (for admin metrics).
 */
export const getCompletionsForSession = async (sessionId) => {
  if (!db || !sessionId) return [];

  const snapshot = await getDocs(
    query(
      collection(db, QUIZ_COMPLETIONS_COLLECTION),
      where('sessionId', '==', sessionId)
    )
  );
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Get completions grouped by user email from an already-loaded list.
 */
const groupCompletionsByUser = (all) => {
  const byUser = {};
  all.forEach((c) => {
    const email = c.userEmail || 'unknown';
    if (!byUser[email]) {
      byUser[email] = { userEmail: email, count: 0, totalScore: 0, scoredCount: 0 };
    }
    byUser[email].count += 1;
    if (c.totalQuestions > 0) {
      byUser[email].totalScore += (c.score / c.totalQuestions) * 100;
      byUser[email].scoredCount += 1;
    }
  });

  return Object.values(byUser)
    .map((u) => ({
      userEmail: u.userEmail,
      count: u.count,
      avgScore: u.scoredCount > 0 ? Math.round(u.totalScore / u.scoredCount) : 0,
    }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Get completion stats for dashboard metrics.
 * For admin: one full collection read (needed for platform totals).
 * For consultant: email-scoped query only — never scan the whole collection.
 */
export const getCompletionStats = async (userEmail, isAdmin = false) => {
  const empty = { totalCompletions: 0, myCompletions: 0, avgScore: 0, uniqueUsers: 0, byUser: [] };
  if (!db) return empty;

  const email = (userEmail || '').toLowerCase();
  const avgFrom = (rows) => {
    const withScore = rows.filter((c) => c.totalQuestions > 0);
    if (!withScore.length) return 0;
    return Math.round(
      withScore.reduce((sum, c) => sum + (c.score / c.totalQuestions) * 100, 0) / withScore.length
    );
  };

  // Consultants / non-admin: only their own docs
  if (!isAdmin) {
    if (!email) return empty;
    const mineSnap = await getDocs(
      query(collection(db, QUIZ_COMPLETIONS_COLLECTION), where('userEmail', '==', email))
    );
    const mine = mineSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return {
      totalCompletions: mine.length,
      myCompletions: mine.length,
      avgScore: avgFrom(mine),
      uniqueUsers: 0,
      byUser: [],
    };
  }

  // Admin: single full scan for platform stats and per-consultant chart
  const completionsSnapshot = await getDocs(collection(db, QUIZ_COMPLETIONS_COLLECTION));
  const all = completionsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  const myCompletions = email ? all.filter((c) => (c.userEmail || '').toLowerCase() === email) : [];
  const byUser = groupCompletionsByUser(all);

  return {
    totalCompletions: all.length,
    myCompletions: myCompletions.length,
    avgScore: avgFrom(all),
    uniqueUsers: new Set(all.map((c) => c.userEmail).filter(Boolean)).size,
    byUser,
  };
};

/**
 * Get completions grouped by user (for admin metrics graph).
 * Returns [{ userEmail, count, avgScore }] sorted by count desc.
 */
export const getCompletionsByUser = async () => {
  if (!db) return [];

  const snapshot = await getDocs(collection(db, QUIZ_COMPLETIONS_COLLECTION));
  const all = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return groupCompletionsByUser(all);
};

/**
 * Get all sessions created by an admin.
 */
export const getSessionsForAdmin = async (adminEmail) => {
  if (!db) return [];

  const email = (adminEmail || '').toLowerCase();
  if (!email) return [];

  const snapshot = await getDocs(
    query(
      collection(db, QUIZ_SESSIONS_COLLECTION),
      where('createdBy', '==', email)
    )
  );

  const sessions = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return sessions.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
};

/**
 * Get a single session by ID.
 * Returns null if not found.
 */
export const getSessionById = async (sessionId) => {
  if (!db || !sessionId) return null;

  const docRef = doc(db, QUIZ_SESSIONS_COLLECTION, sessionId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;

  return { id: snap.id, ...snap.data() };
};

/**
 * Check if a user is assigned to a session.
 */
export const isUserAssignedToSession = async (sessionId, userEmail) => {
  const session = await getSessionById(sessionId);
  if (!session) return false;

  const email = (userEmail || '').toLowerCase();
  return (session.assignedUserEmails || []).includes(email);
};

/**
 * Get a single completion by id (for report view).
 * @returns {Promise<{id: string, ...} | null>}
 */
export const getCompletionById = async (completionId) => {
  if (!db || !completionId) return null;
  const docRef = doc(db, QUIZ_COMPLETIONS_COLLECTION, completionId);
  const snap = await getDoc(docRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Get completions for reports. Admin sees all; consultant sees only their own.
 * Non-admin uses an email-scoped query (no full-collection scan).
 */
export const getCompletionsForReport = async (userEmail, isAdmin = false) => {
  if (!db) return [];

  const email = (userEmail || '').toLowerCase();
  let snapshot;
  if (!isAdmin) {
    if (!email) return [];
    snapshot = await getDocs(
      query(collection(db, QUIZ_COMPLETIONS_COLLECTION), where('userEmail', '==', email))
    );
  } else {
    snapshot = await getDocs(collection(db, QUIZ_COMPLETIONS_COLLECTION));
  }

  const completions = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return completions.sort((a, b) => {
    const ta = a.completedAt?.toMillis?.() ?? 0;
    const tb = b.completedAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
};

// --- Interview Slots (calendar booking) ---
const normalizeSlotEmail = (email) => (email || '').toLowerCase().trim();

export const createInterviewSlot = async (slot) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!slot.bookedBy) throw new Error('bookedBy is required');
  if (!slot.startTime || !slot.endTime) throw new Error('startTime and endTime are required');

  const start = slot.startTime instanceof Date ? Timestamp.fromDate(slot.startTime) : slot.startTime;
  const end = slot.endTime instanceof Date ? Timestamp.fromDate(slot.endTime) : slot.endTime;

  const docRef = await addDoc(collection(db, INTERVIEW_SLOTS_COLLECTION), {
    bookedBy: normalizeSlotEmail(slot.bookedBy),
    startTime: start,
    endTime: end,
    timezone: slot.timezone || '',
    name: slot.name || '',
    email: slot.email || '',
    client: slot.client || '',
    vendor: slot.vendor || '',
    mode: slot.mode || 'video',
    durationMinutes: slot.durationMinutes || 30,
    interviewThrough: slot.interviewThrough || '',
    interviewThroughOther: slot.interviewThroughOther || '',
    title: slot.title || '',
    createdAt: serverTimestamp(),
  });

  return { id: docRef.id, ...slot };
};

export const getMyInterviewSlots = async (userEmail, rangeStart, rangeEnd) => {
  if (!db) return [];
  const email = normalizeSlotEmail(userEmail);
  if (!email) return [];

  const start = rangeStart instanceof Date ? Timestamp.fromDate(rangeStart) : rangeStart;
  const end = rangeEnd instanceof Date ? Timestamp.fromDate(rangeEnd) : rangeEnd;

  const q = query(
    collection(db, INTERVIEW_SLOTS_COLLECTION),
    where('bookedBy', '==', email),
    where('startTime', '<=', end),
    orderBy('startTime', 'asc')
  );

  const snapshot = await getDocs(q);
  const slots = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return slots.filter((s) => s.endTime && s.endTime.toMillis && s.endTime.toMillis() >= start.toMillis());
};

export const getAllInterviewSlotsInRange = async (rangeStart, rangeEnd) => {
  if (!db) return [];
  const start = rangeStart instanceof Date ? Timestamp.fromDate(rangeStart) : rangeStart;
  const end = rangeEnd instanceof Date ? Timestamp.fromDate(rangeEnd) : rangeEnd;

  const q = query(
    collection(db, INTERVIEW_SLOTS_COLLECTION),
    where('startTime', '<=', end),
    orderBy('startTime', 'asc')
  );

  const snapshot = await getDocs(q);
  const slots = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return slots.filter((s) => s.endTime && s.endTime.toMillis && s.endTime.toMillis() >= start.toMillis());
};

export const updateInterviewSlot = async (slotId, slot) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!slotId) throw new Error('slotId is required');
  if (!slot.startTime || !slot.endTime) throw new Error('startTime and endTime are required');

  const start = slot.startTime instanceof Date ? Timestamp.fromDate(slot.startTime) : slot.startTime;
  const end = slot.endTime instanceof Date ? Timestamp.fromDate(slot.endTime) : slot.endTime;

  await updateDoc(doc(db, INTERVIEW_SLOTS_COLLECTION, slotId), {
    bookedBy: normalizeSlotEmail(slot.bookedBy),
    startTime: start,
    endTime: end,
    timezone: slot.timezone || '',
    name: slot.name || '',
    email: slot.email || '',
    client: slot.client || '',
    vendor: slot.vendor || '',
    mode: slot.mode || 'video',
    durationMinutes: slot.durationMinutes || 30,
    interviewThrough: slot.interviewThrough || '',
    interviewThroughOther: slot.interviewThroughOther || '',
    title: slot.title || '',
    updatedAt: serverTimestamp(),
  });
  return { id: slotId, ...slot };
};

export const deleteInterviewSlot = async (slotId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!slotId) throw new Error('slotId is required');
  await deleteDoc(doc(db, INTERVIEW_SLOTS_COLLECTION, slotId));
};

// --- Timesheet / Worklogs ---
const WORKLOGS_COLLECTION = 'worklogs';

export function getWeekRange(date) {
  const d = new Date(date);
  const day = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function getMonthRange(date) {
  const d = new Date(date);
  const start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

export async function getWorklogsForRange(userEmail, startDate, endDate) {
  if (!db) return [];
  const startStr = toDateString(startDate);
  const endStr = toDateString(endDate);
  const col = collection(db, WORKLOGS_COLLECTION);
  const q = query(col, where('userEmail', '==', (userEmail || '').toLowerCase().trim()));
  const snap = await getDocs(q);
  const all = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  return all
    .filter((w) => w.date >= startStr && w.date <= endStr)
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
}

export function toDateString(date) {
  const d = new Date(date);
  return d.toISOString().slice(0, 10);
}

export async function getWorklogsForWeek(userEmail, weekStart) {
  if (!db) return [];
  const { start, end } = getWeekRange(weekStart);
  const startStr = toDateString(start);
  const endStr = toDateString(end);
  const col = collection(db, WORKLOGS_COLLECTION);
  const q = query(
    col,
    where('userEmail', '==', (userEmail || '').toLowerCase().trim())
  );
  const snap = await getDocs(q);
  const all = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  return all
    .filter((w) => w.date >= startStr && w.date <= endStr)
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
}

export async function saveWorklog(data) {
  if (!db) throw new Error('Firestore not configured');
  const payload = {
    userEmail: (data.userEmail || '').toLowerCase().trim(),
    date: data.date,
    startTime: data.startTime || '09:00',
    endTime: data.endTime || '17:00',
    hours: typeof data.hours === 'number' ? data.hours : parseFloat(data.hours) || 0,
    projectName: data.projectName || '',
    workingFor: data.workingFor || '',
    comment: data.comment || '',
    createdAt: Timestamp.now(),
  };
  const ref = await addDoc(collection(db, WORKLOGS_COLLECTION), payload);
  return ref.id;
}
